/** @format */

export type PatternType = 'grid' | 'mosaic' | 'triangles' | 'space-invaders' | 'circles'
export type LogoConfig = {
  text: string
  size?: number
  pattern?: PatternType
}

/**
 * Small deterministic logo generator.
 * Returns a consistent logo given the same input text.
 *
 * Supported pattern types are:
 *  - 'grid'
 *  - 'mosaic'
 *  - 'triangles'
 *  - 'space-invaders'
 *  - 'circles'
 *
 * @example
 *   const svg = LogoGenerator.generateLogoSVG({ text: 'alice', size: 150, pattern: 'space-invaders' });
 */
class LogoGenerator {
  /**
   * Array of color palettes. Each sub-array contains 4 hex color codes.
   * Used for logo background and pattern colors.
   *
   * @private
   */
  private static readonly PALETTES: string[][] = [
    ['#E84651', '#E846BA', '#E84686', '#FFF5F9'],
    ['#FB4C01', '#FF8D14', '#C92418', '#FFF9F3'],
    ['#E2FFDE', '#5DD7B6', '#3FBF86', '#5DD64B'],
    ['#2E9AED', '#64C3FF', '#A6E1FF', '#E9F8FF'],
    ['#7C6BFF', '#C08CFF', '#FFB6F0', '#FFF0FF'],
    // additional palettes (pastels)
    ['#FFD6E0', '#FFB3C6', '#FF87A2', '#FFF7F9'],
    ['#FFF5BA', '#FFE599', '#FFC857', '#FFFDF0'],
    // vivid duotones
    ['#0F172A', '#06B6D4', '#7DD3FC', '#E6FCFF'],
    ['#1F2937', '#F97316', '#FB923C', '#FFF7ED'],
    // nature
    ['#0B3D2E', '#137F5B', '#38C172', '#ECFFF5'],
    // muted
    ['#2D3748', '#4A5568', '#718096', '#F7FAFC'],
    // monochrome warm
    ['#111827', '#374151', '#9CA3AF', '#F3F4F6'],
    // cool neon
    ['#00F5A0', '#7C3AED', '#06B6D4', '#001219'],
    // retro
    ['#FF6B6B', '#FFE66D', '#4472CA', '#F0F7FF'],
    // soft gradients
    ['#FFEFBA', '#FFFFFF', '#D4FC79', '#F0FFF4'],
  ]

  /**
   * Hashes a string into a consistent 32-bit unsigned integer using the djb2 algorithm.
   * Used to seed the pattern, color, and randomness.
   *
   * @param str - The text to hash.
   * @returns The 32-bit unsigned integer hash.
   * @private
   */
  private static djb2Hash(str: string): number {
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) ^ str.charCodeAt(i)
    }
    return Math.abs(hash >>> 0)
  }

  /**
   * Chooses a color palette from PALETTES based on the given hash.
   *
   * @param hash - A deterministic value per username/text.
   * @returns An array of four hex color strings.
   * @private
   */
  private static pickPalette(hash: number): string[] {
    return LogoGenerator.PALETTES[hash % LogoGenerator.PALETTES.length]
  }

  /**
   * Simple deterministic PRNG: returns a float in [0, 1) based on the input and a salt.
   * Used for fine-grained layout randomness.
   *
   * @param hash - Main seed integer (derived from text).
   * @param salt - Extra ingredient integer to change per-grid cell or feature.
   * @returns A float in [0, 1). Same hash+salt combination always returns the same value.
   * @private
   */
  private static rnd(hash: number, salt: number): number {
    const val = (hash ^ (salt * 0x9e3779b9)) >>> 0
    return (val % 1000) / 1000
  }

  /**
   * Generates a deterministic SVG logo for the input text. The SVG will always be
   * the same for the same inputs.
   *
   * @param config - An object containing text, optional size, and optional pattern.
   *   @property text - The string to generate an logo from.
   *   @property size - Width/height of the SVG in pixels. Default is 150.
   *   @property pattern - Optional: force the pattern style. One of 'grid', 'mosaic', 'triangles', 'space-invaders', 'circles'. *If not specified or unrecognized, the pattern is chosen automatically from text.
   * @returns The SVG image as a string.
   */
  public static generateLogoSVG(config: LogoConfig): string {
    const seed = LogoGenerator.djb2Hash(config.text || '_')
    const palette = LogoGenerator.pickPalette(seed)
    const size = config.size ?? 150
    // Map string pattern type to internal index used in code
    const patternMap: Record<PatternType, number> = {
      grid: 0,
      mosaic: 1,
      triangles: 2,
      'space-invaders': 3,
      circles: 4,
    }
    let finalPatternType: number
    if (config.pattern && config.pattern in patternMap) {
      finalPatternType = patternMap[config.pattern]
    } else {
      finalPatternType = seed % 5
    }
    const variant = (seed >> 3) % 5

    if (finalPatternType === 4) return LogoGenerator.circlePattern(seed, palette, size)
    if (finalPatternType === 3) return LogoGenerator.spaceInvaderPattern(seed, palette, size, variant)
    if (finalPatternType === 2) return LogoGenerator.trianglePattern(seed, palette, size, variant)
    // grid and mosaic handled together
    return LogoGenerator.gridPattern(seed, palette, size, finalPatternType, variant)
  }

  /**
   * Returns the avatar SVG as a data URL suitable for use in <img src="..." />.
   * @param config LogoConfig
   * @returns {string} Data URL for use in an <img> tag.
   */
  public static getLogoDataUrl(config: LogoConfig): string {
    const svg = LogoGenerator.generateLogoSVG(config)
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
  }

  /**
   * Generates a circle-based pattern logo SVG.
   *
   * @param seed - PRNG seed based on input text.
   * @param palette - Array of 4 colors to use.
   * @param size - SVG width and height in px.
   * @returns SVG string for the circles logo pattern.
   * @private
   */
  private static circlePattern(seed: number, palette: string[], size: number): string {
    const parts: string[] = []
    parts.push(`<?xml version="1.0"?>`)
    parts.push(
      `<svg viewBox="0 0 ${size} ${size}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">`
    )
    const bgColor = palette[(seed + 1) % palette.length]
    parts.push(`<rect x="0" y="0" width="${size}" height="${size}" fill="${bgColor}"/>`)
    const grid = 8
    const cell = size / grid
    const half = Math.ceil(grid / 2)
    for (let y = 0; y < grid; y++) {
      for (let x = 0; x < half; x++) {
        const local = Math.floor(LogoGenerator.rnd(seed, x * 997 + y * 991) * 10)
        const draw = local % 3 !== 0
        const color = palette[(seed + x * 13 + y * 7) % palette.length]
        if (draw) {
          const rx = x * cell
          const ry = y * cell
          const mx = (grid - 1 - x) * cell
          const cx = rx + cell / 2
          const cy = ry + cell / 2
          const r = cell * 0.45
          parts.push(`<circle cx="${cx}" cy="${cy}" r="${r}" style="fill:${color}" />`)
          parts.push(`<circle cx="${mx + cell / 2}" cy="${cy}" r="${r}" style="fill:${color}" />`)
        }
      }
    }
    parts.push(`</svg>`)
    return parts.join('\n')
  }

  /**
   * Generates a "space invaders" sprite pattern logo SVG.
   *
   * @param seed - PRNG seed based on input text.
   * @param palette - Array of 4 colors to use.
   * @param size - SVG width/height.
   * @param variant - Sub-style for variation.
   * @returns SVG string for space invaders logo pattern.
   * @private
   */
  private static spaceInvaderPattern(seed: number, palette: string[], size: number, variant: number): string {
    const parts: string[] = []
    parts.push(`<?xml version="1.0"?>`)
    parts.push(
      `<svg viewBox="0 0 ${size} ${size}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">`
    )
    const bgColor = palette[(seed + 1) % palette.length]
    parts.push(`<rect x="0" y="0" width="${size}" height="${size}" fill="${bgColor}"/>`)
    const spriteWidth = 11,
      spriteHeight = 12
    const pixelSize = Math.min(size / spriteWidth, size / spriteHeight)
    const offsetX = (size - spriteWidth * pixelSize) / 2
    const offsetY = (size - spriteHeight * pixelSize) / 2
    const halfW = Math.floor(spriteWidth / 2)
    function colorForTier(tier: number) {
      const idx = (seed + tier * 7) % palette.length
      return palette[idx]
    }
    const densityBoost = variant === 0 ? 0.12 : variant === 1 ? 0.06 : variant === 2 ? 0.18 : variant === 3 ? 0.1 : 0.14
    const spikeChance = variant === 0 ? 0.25 : variant === 1 ? 0.15 : 0.2
    const limbChance = variant === 1 ? 0.28 : 0.2
    const eyeRow = 2 + ((seed >> 5) % 3)
    const eyeGap = 1 + ((seed >> 7) % 2)
    for (let y = 0; y < spriteHeight; y++) {
      const t = Math.abs((y - (spriteHeight - 1) / 2) / ((spriteHeight - 1) / 2))
      const baseDensity = 0.28 + densityBoost * (1 - t)
      for (let x = 0; x <= halfW; x++) {
        const v = LogoGenerator.rnd(seed, 1003 + y * 137 + x * 97 + variant * 811)
        let tier = 0
        if (v < baseDensity) {
          tier = v < baseDensity * 0.45 ? 2 : 1
        }
        if (variant === 3) {
          const mid = Math.abs(x - halfW) / halfW
          if (
            Math.abs(y - Math.floor(spriteHeight / 2)) <= 1 &&
            mid < 0.8 &&
            LogoGenerator.rnd(seed, y * 233 + x * 17) < 0.6
          ) {
            tier = Math.max(tier, 2)
          }
        }
        if (
          LogoGenerator.rnd(seed, 7000 + y * 31) < limbChance &&
          (y === Math.floor(spriteHeight * 0.55) || y === Math.floor(spriteHeight * 0.6))
        ) {
          if (x >= halfW - 2) tier = Math.max(tier, 1)
        }
        if (y < 3 && LogoGenerator.rnd(seed, 9000 + x * 19) < spikeChance) {
          if (x === halfW) tier = Math.max(tier, 2)
        }
        if (y >= spriteHeight - 2 && LogoGenerator.rnd(seed, 12000 + x * 13) < 0.55) {
          if (x === 1 || x === halfW - 1) tier = Math.max(tier, 2)
        }
        if (y === eyeRow) {
          const isEyeCol = x === halfW - eyeGap || x === halfW + eyeGap - spriteWidth
          if (isEyeCol) {
            if (LogoGenerator.rnd(seed, 30000 + x) < 0.4) {
              tier = 0
            } else {
              tier = 4
            }
          }
        }
        if (tier > 0) {
          const color = colorForTier(tier)
          const px = offsetX + x * pixelSize,
            py = offsetY + y * pixelSize
          parts.push(`<rect x="${px}" y="${py}" width="${pixelSize}" height="${pixelSize}" fill="${color}" />`)
          const mx = spriteWidth - 1 - x
          if (mx !== x) {
            const mpx = offsetX + mx * pixelSize
            parts.push(`<rect x="${mpx}" y="${py}" width="${pixelSize}" height="${pixelSize}" fill="${color}" />`)
          }
        }
      }
    }
    if (variant === 1) {
      const stripe = palette[(seed + 2) % palette.length]
      parts.push(
        `<defs><pattern id="px-${seed}" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(20)"><rect width="5" height="10" fill="${stripe}" opacity="0.06" /></pattern></defs>`
      )
      parts.push(`<rect x="0" y="0" width="${size}" height="${size}" fill="url(#px-${seed})" />`)
    }
    parts.push(`</svg>`)
    return parts.join('\n')
  }

  /**
   * Generates a multi-variant triangles/honeycomb pattern SVG logo.
   *
   * @param seed - PRNG seed based on text.
   * @param palette - 4 color palette.
   * @param size - SVG width/height.
   * @param variant - Controls triangle style/arrangement.
   * @returns SVG string for triangles/honeycomb logo pattern.
   * @private
   */
  private static trianglePattern(seed: number, palette: string[], size: number, variant: number): string {
    const parts: string[] = []
    parts.push(`<?xml version="1.0"?>`)
    parts.push(
      `<svg viewBox="0 0 ${size} ${size}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">`
    )

    const bg = palette[(seed + 3) % palette.length]
    parts.push(`<rect x="0" y="0" width="${size}" height="${size}" fill="${bg}"/>`)

    // Helper to draw a triangle
    function drawTriangle(
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      x3: number,
      y3: number,
      color: string,
      opacity = 1
    ) {
      return `<polygon points="${x1},${y1} ${x2},${y2} ${x3},${y3}" fill="${color}" opacity="${opacity}" />`
    }

    if (variant === 0) {
      // Classic tessellation with enhanced depth and layering
      const cols = 8
      const rows = 8
      const cellW = size / cols
      const cellH = size / rows

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * cellW
          const y = r * cellH
          const fillProb = LogoGenerator.rnd(seed, r * 47 + c * 31)

          if (fillProb > 0.35) {
            const isUp = (r + c) % 2 === 0
            const colorIdx = Math.floor(LogoGenerator.rnd(seed, r * 73 + c * 41) * palette.length)
            const color = palette[(seed + colorIdx) % palette.length]

            if (isUp) {
              // Upward triangle
              parts.push(drawTriangle(x, y + cellH, x + cellW / 2, y, x + cellW, y + cellH, color))
              // Inner triangle for depth
              if (fillProb > 0.65) {
                const inset = 0.15
                const innerColor = palette[(seed + colorIdx + 1) % palette.length]
                parts.push(
                  drawTriangle(
                    x + cellW * inset,
                    y + cellH * (1 - inset),
                    x + cellW / 2,
                    y + cellH * inset,
                    x + cellW * (1 - inset),
                    y + cellH * (1 - inset),
                    innerColor,
                    0.8
                  )
                )
              }
            } else {
              // Downward triangle
              parts.push(drawTriangle(x, y, x + cellW / 2, y + cellH, x + cellW, y, color))
              // Inner triangle for depth
              if (fillProb > 0.65) {
                const inset = 0.15
                const innerColor = palette[(seed + colorIdx + 1) % palette.length]
                parts.push(
                  drawTriangle(
                    x + cellW * inset,
                    y + cellH * inset,
                    x + cellW / 2,
                    y + cellH * (1 - inset),
                    x + cellW * (1 - inset),
                    y + cellH * inset,
                    innerColor,
                    0.8
                  )
                )
              }
            }
          }
        }
      }
    } else if (variant === 1) {
      // Chevron/Zigzag pattern
      const segments = 12
      const segW = size / segments
      const segH = size / 6

      for (let s = 0; s < segments; s++) {
        const x = s * segW
        const isUp = s % 2 === 0
        const fillProb = LogoGenerator.rnd(seed, s * 59 + 100)

        if (fillProb > 0.3) {
          for (let row = 0; row < 6; row++) {
            const y = row * segH
            const colorIdx = (seed + s * 7 + row * 11) % palette.length
            const color = palette[colorIdx]

            if (isUp) {
              parts.push(drawTriangle(x, y + segH, x + segW / 2, y, x + segW, y + segH, color))
            } else {
              parts.push(drawTriangle(x, y, x + segW / 2, y + segH, x + segW, y, color))
            }
          }
        }
      }
    } else if (variant === 2) {
      // Hexagonal tessellation (triangular honeycomb)
      const hexSize = size / 6
      const hexRadius = hexSize * 0.866 // sqrt(3)/2

      for (let row = 0; row < 7; row++) {
        const y = row * hexSize * 0.75
        const cols = row % 2 === 0 ? 6 : 5
        const offsetX = row % 2 === 0 ? 0 : hexSize * 0.866

        for (let col = 0; col < cols; col++) {
          const x = offsetX + col * hexSize * 1.732
          const fillProb = LogoGenerator.rnd(seed, row * 67 + col * 43)

          if (fillProb > 0.4) {
            // Draw 6 triangles forming a hexagon
            const centerX = x + hexSize * 0.866
            const centerY = y + hexSize * 0.5
            const colorIdx = (seed + row * 13 + col * 17) % palette.length
            const color = palette[colorIdx]

            for (let i = 0; i < 6; i++) {
              const angle1 = (i * Math.PI) / 3
              const angle2 = ((i + 1) * Math.PI) / 3
              const x1 = centerX + hexRadius * Math.cos(angle1)
              const y1 = centerY + hexRadius * Math.sin(angle1)
              const x2 = centerX + hexRadius * Math.cos(angle2)
              const y2 = centerY + hexRadius * Math.sin(angle2)
              parts.push(drawTriangle(centerX, centerY, x1, y1, x2, y2, color))
            }
          }
        }
      }
    } else if (variant === 3) {
      // Concentric/radial triangles
      const centerX = size / 2
      const centerY = size / 2
      const rings = 8
      const trianglesPerRing = [6, 12, 18, 24, 30, 36, 42, 48]

      for (let ring = 0; ring < rings; ring++) {
        const ringRadius = (ring + 1) * (size / (rings + 2))
        const triangles = trianglesPerRing[ring]
        const angleStep = (2 * Math.PI) / triangles

        for (let t = 0; t < triangles; t++) {
          const fillProb = LogoGenerator.rnd(seed, ring * 71 + t * 37)
          if (fillProb > 0.45) {
            const angle = t * angleStep
            const innerRadius = ring * (size / (rings + 2))
            const colorIdx = (seed + ring * 19 + t * 7) % palette.length
            const color = palette[colorIdx]

            // Outer point
            const x1 = centerX + ringRadius * Math.cos(angle)
            const y1 = centerY + ringRadius * Math.sin(angle)
            // Inner points
            const x2 = centerX + innerRadius * Math.cos(angle - angleStep / 2)
            const y2 = centerY + innerRadius * Math.sin(angle - angleStep / 2)
            const x3 = centerX + innerRadius * Math.cos(angle + angleStep / 2)
            const y3 = centerY + innerRadius * Math.sin(angle + angleStep / 2)

            parts.push(drawTriangle(x1, y1, x2, y2, x3, y3, color))
          }
        }
      }
    } else {
      // Variant 4: Staggered layered triangles with varying sizes
      const baseSize = size / 10
      const layers = 3

      for (let layer = 0; layer < layers; layer++) {
        const layerSize = baseSize * (1.5 - layer * 0.3)
        const cols = Math.floor(size / layerSize) + 2
        const rows = Math.floor(size / layerSize) + 2
        const offset = layerSize * 0.3 * layer

        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const fillProb = LogoGenerator.rnd(seed, layer * 83 + r * 53 + c * 29)
            if (fillProb > 0.5) {
              const x = offset + c * layerSize + (r % 2) * (layerSize / 2)
              const y = offset + r * layerSize * 0.866
              const colorIdx = (seed + layer * 23 + r * 31 + c * 19) % palette.length
              const color = palette[colorIdx]

              // Random orientation
              const orientation = Math.floor(LogoGenerator.rnd(seed, layer * 97 + r * 61 + c * 41) * 4)
              const triSize = layerSize * (0.7 + LogoGenerator.rnd(seed, r * 101 + c * 73) * 0.3)

              if (orientation === 0) {
                parts.push(drawTriangle(x, y, x + triSize / 2, y + triSize * 0.866, x + triSize, y, color, 0.85))
              } else if (orientation === 1) {
                parts.push(
                  drawTriangle(
                    x,
                    y + triSize * 0.866,
                    x + triSize / 2,
                    y,
                    x + triSize,
                    y + triSize * 0.866,
                    color,
                    0.85
                  )
                )
              } else if (orientation === 2) {
                parts.push(
                  drawTriangle(
                    x + triSize / 2,
                    y,
                    x,
                    y + triSize * 0.866,
                    x + triSize / 2,
                    y + triSize * 0.866,
                    color,
                    0.85
                  )
                )
              } else {
                parts.push(
                  drawTriangle(
                    x + triSize / 2,
                    y,
                    x + triSize,
                    y + triSize * 0.866,
                    x + triSize / 2,
                    y + triSize * 0.866,
                    color,
                    0.85
                  )
                )
              }
            }
          }
        }
      }
    }

    parts.push(`</svg>`)
    return parts.join('\n')
  }

  /**
   * Generates grid or mosaic pattern SVG (rect, circle, rounded, diamond, outline).
   *
   * @param seed - PRNG seed.
   * @param palette - Colors to use.
   * @param size - SVG width/height.
   * @param finalPatternType - 0 for grid, 1 for mosaic.
   * @param variant - Variant for mosaic sub-style.
   * @returns SVG string of the pattern.
   * @private
   */
  private static gridPattern(
    seed: number,
    palette: string[],
    size: number,
    finalPatternType: number,
    variant: number
  ): string {
    const grid = finalPatternType === 0 ? 6 : 12 // grid size 6 or 12
    const cell = size / grid
    // choose a shape variant for the grid: 0=rect,1=circle,2=rounded,3=diamond,4=outline
    // Grid pattern (0) should always use rectangular shapes; Mosaic (1) can vary
    const shape = finalPatternType === 0 ? 0 : variant // Grid always uses rectangles, Mosaic uses variant-based shapes

    const svg: string[] = []
    svg.push(`<?xml version="1.0"?>`)
    svg.push(
      `<svg viewBox="0 0 ${size} ${size}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">`
    )

    // background color is the palette light variant
    const bgColor = palette[(seed + 1) % palette.length]
    svg.push(`<rect x="0" y="0" width="${size}" height="${size}" fill="${bgColor}"/>`)

    // optional diagonal stripes overlay for one of the variants
    if (variant === 4) {
      const stripe = palette[(seed + 2) % palette.length]
      svg.push(
        `<defs><pattern id="s-${seed}" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(25)"><rect width="6" height="12" fill="${stripe}" opacity="0.08" /></pattern></defs>`
      )
      svg.push(`<rect x="0" y="0" width="${size}" height="${size}" fill="url(#s-${seed})" />`)
    }

    // We'll do horizontal symmetry for a pleasing logo
    const half = Math.ceil(grid / 2)
    for (let y = 0; y < grid; y++) {
      for (let x = 0; x < half; x++) {
        const local = Math.floor(LogoGenerator.rnd(seed, x * 997 + y * 991) * 10)
        // choose whether to draw a filled element or leave it background
        const draw = local % 3 !== 0 // ~66% filled
        const color = palette[(seed + x * 13 + y * 7) % palette.length]
        if (draw) {
          const rx = x * cell
          const ry = y * cell
          const mx = (grid - 1 - x) * cell

          // shape variants
          if (shape === 0) {
            svg.push(`<rect x="${rx}" y="${ry}" width="${cell}" height="${cell}" style="fill:${color}" />`)
            svg.push(`<rect x="${mx}" y="${ry}" width="${cell}" height="${cell}" style="fill:${color}" />`)
          } else if (shape === 1) {
            const cx = rx + cell / 2
            const cy = ry + cell / 2
            const r = cell * 0.45
            svg.push(`<circle cx="${cx}" cy="${cy}" r="${r}" style="fill:${color}" />`)
            svg.push(`<circle cx="${mx + cell / 2}" cy="${cy}" r="${r}" style="fill:${color}" />`)
          } else if (shape === 2) {
            const rxr = Math.max(2, Math.floor(cell * 0.12))
            svg.push(
              `<rect x="${rx}" y="${ry}" width="${cell}" height="${cell}" rx="${rxr}" ry="${rxr}" style="fill:${color}" />`
            )
            svg.push(
              `<rect x="${mx}" y="${ry}" width="${cell}" height="${cell}" rx="${rxr}" ry="${rxr}" style="fill:${color}" />`
            )
          } else if (shape === 3) {
            // diamond via rotated square
            const cx = rx + cell / 2
            const cy = ry + cell / 2
            svg.push(
              `<rect x="${cx - cell / 2}" y="${cy - cell / 2}" width="${cell}" height="${cell}" style="fill:${color};transform-origin:${cx}px ${cy}px;transform:rotate(45deg)" />`
            )
            const mcx = mx + cell / 2
            svg.push(
              `<rect x="${mcx - cell / 2}" y="${cy - cell / 2}" width="${cell}" height="${cell}" style="fill:${color};transform-origin:${mcx}px ${cy}px;transform:rotate(45deg)" />`
            )
          } else {
            // outline shape
            svg.push(
              `<rect x="${rx}" y="${ry}" width="${cell}" height="${cell}" style="fill:none;stroke:${color};stroke-width:${Math.max(1, size / 120)}" />`
            )
            svg.push(
              `<rect x="${mx}" y="${ry}" width="${cell}" height="${cell}" style="fill:none;stroke:${color};stroke-width:${Math.max(1, size / 120)}" />`
            )
          }
        }
      }
    }

    svg.push(`</svg>`)
    return svg.join('\n')
  }
}

export default LogoGenerator
